import React, { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../components/sub-comp/Navbar";
import Footer from "../components/sub-comp/MainFooter";
import { getNewsById, getNewsList } from "../data/news";

const ShareButtons = ({ url, title }) => {
  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        alert("Link copied to clipboard");
      }
    } catch {}
  };
  return (
    <button onClick={share} className="px-4 py-2 rounded-full bg-custom-pink text-white hover:bg-purple-700 transition">
      Share
    </button>
  );
};

const NewsDetail = () => {
  const { id } = useParams();

  const article = useMemo(() => getNewsById(id), [id]);
  const all = useMemo(() => getNewsList(), []);

  // Basic SEO: set page title and meta description
  useEffect(() => {
    if (!article) return;
    const prevTitle = document.title;
    document.title = `${article.title} | JRIIT News`;

    const metaDescription = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement('meta');
      m.setAttribute('name', 'description');
      document.head.appendChild(m);
      return m;
    })();
    const prevDesc = metaDescription.getAttribute('content');
    metaDescription.setAttribute('content', article.excerpt || article.description || article.title);

    return () => {
      document.title = prevTitle;
      if (prevDesc !== null && prevDesc !== undefined) {
        metaDescription.setAttribute('content', prevDesc);
      }
    };
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-8">The article you’re looking for doesn’t exist or may have been moved.</p>
          <Link to="/news" className="inline-block px-6 py-3 rounded-full bg-custom-pink text-white hover:bg-purple-700 transition">Back to News</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const url = `${window.location.origin}/news/${article.id}`;
  const related = all.filter((n) => n.category === article.category && n.id !== article.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-r from-custom-pink to-purple-800 text-white">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <nav className="text-white/80 text-sm mb-4">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/news" className="hover:text-white">News</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{article.title}</span>
          </nav>
          <h1 className="text-3xl md:text-5xl font-bold mb-4">{article.title}</h1>
          <div className="flex flex-wrap gap-3 items-center text-white/90">
            <span className="px-3 py-1 bg-gold text-black rounded-full font-bold text-sm">{article.category}</span>
            <span className="text-sm">{article.date}</span>
            <span className="text-sm">{article.author}</span>
            <span className="text-sm">{article.readTime}</span>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4">
          <img src={article.featuredImage || article.image} alt={article.title} className="w-full h-[300px] md:h-[480px] object-cover rounded-xl shadow" />
        </div>
      </section>

      {/* Article Body */}
      <section className="py-12">
        <div className="container mx-auto px-4 grid grid-cols-1 lg:grid-cols-4 gap-8">
          <article className="lg:col-span-3 bg-white rounded-xl shadow p-6">
            <div className="flex flex-wrap gap-2 mb-4">
              {Array.isArray(article.tags) && article.tags.map((tag) => (
                <span key={tag} className="px-2 py-1 bg-custom-pink/10 text-custom-pink text-xs rounded-full">{tag}</span>
              ))}
            </div>
            <div className="prose max-w-none">
              <p className="text-gray-800 leading-relaxed whitespace-pre-line">{article.content || article.description || article.excerpt}</p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3 items-center">
              <ShareButtons url={url} title={article.title} />
              <button onClick={() => window.print()} className="px-4 py-2 rounded-full bg-gray-200 text-gray-800 hover:bg-gray-300 transition">Print</button>
              <Link to="/news" className="ml-auto px-4 py-2 rounded-full bg-gold text-black hover:bg-yellow-400 transition">Back to News</Link>
            </div>
          </article>

          {/* Sidebar: Related */}
          <aside className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow p-5">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Related Articles</h3>
              <div className="space-y-4">
                {related.map((n) => (
                  <Link key={n.id} to={`/news/${n.id}`} className="flex gap-3 group">
                    <img src={n.image} alt={n.title} className="w-16 h-16 object-cover rounded" />
                    <div>
                      <p className="text-sm font-semibold group-hover:text-custom-pink">{n.title}</p>
                      <p className="text-xs text-gray-500">{n.date}</p>
                    </div>
                  </Link>
                ))}
                {related.length === 0 && (
                  <p className="text-gray-600 text-sm">No related articles.</p>
                )}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NewsDetail;


